import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  getFormularioPoliticaMunicipalAgriculturaFamiliarByUUID
} from 'api/forms/pacote_formularios.api';
import ContainerPainel from 'components/container_painel';
import { Loading } from 'components/login';
import DocumentComponent from 'components/shared/document_component';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormularioPoliticaMunicipalAgriculturaFamiliarStore } from '../../../../../stores/forms/FormularioPMAF.store';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { ROUTES } from 'routes/constants';

const FormPMAFVisualizar: React.FC = () => { // NOSONAR
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { formulario, setFormulario } =
    useFormularioPoliticaMunicipalAgriculturaFamiliarStore();
  const [loading, setLoading] = useState(true);
  const [openStatus, setOpenStatus] = useState<boolean>(true);
  const [openDetalhes, setOpenDetalhes] = useState<boolean>(true);
  const [openPmaf, setOpenPmaf] = useState<boolean>(true);

  const getFormulario = async () => {
    try {
      if (!uuid) throw new Error('UUID não fornecido');
      const response = await getFormularioPoliticaMunicipalAgriculturaFamiliarByUUID(uuid);
      if (response.dados) setFormulario(response.dados);
    } catch (error) {
      console.error('Erro ao carregar formulário:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTipoInstrumentoTexto = (valor: number | null | undefined) => {
    switch (valor) {
      case 1: return 'Lei';
      case 2: return 'Decreto';
      case 3: return 'Portaria';
      case 4: return 'Resolução';
      default: return 'Não informado';
    }
  };

  const formatarData = (data: string | null) => {
    if (!data) return 'Não informado';
    try {
      return new Date(data).toLocaleDateString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  useEffect(() => {
    getFormulario();
  }, []);

  if (loading) {
    return (
      <ContainerPainel
        pagina='Visualizar Política Municipal da Agricultura Familiar'
        showBackButton={true}
      >
        <Loading />
      </ContainerPainel>
    );
  }

  const possuiPolitica = formulario?.municipio_possui_politica_municipal_de_agricula_familiar_ou_indigena;
  const { label: politicaLabel, color: politicaColor } = (() => {
    if (possuiPolitica === true) return { label: 'Sim', color: 'success' as const };
    if (possuiPolitica === false) return { label: 'Não', color: 'error' as const };
    return { label: 'Não informado', color: 'default' as const };
  })();

  const possuiPMAF = formulario?.possui_plano_municipal_agricultura_familiar;
  const { label: planoLabel, color: planoColor } = (() => {
    if (possuiPMAF === true) return { label: 'Sim', color: 'success' as const };
    if (possuiPMAF === false) return { label: 'Não', color: 'error' as const };
    return { label: 'Não informado', color: 'default' as const };
  })();


  return (
    <ContainerPainel
      pagina='Visualizar Política Municipal da Agricultura Familiar'
      showBackButton={true}
    >
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '24px',
          lineHeight: '133%',
          textAlign: 'center',
          mt: '63px',
          mb: '32px',
        }}
      >
        Visualizar Formulário da Política Municipal da Agricultura Familiar
      </Typography>

      <Stack marginX='4rem' paddingTop='2rem' spacing={2}>
        {/* Bloco: Status da Política Municipal */}
        <Paper elevation={1} sx={{ p: 0 }}>
          <Box
            sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setOpenStatus(!openStatus)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  color: '#000000DE',
                  fontSize: '20px',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {openStatus ? (
                  <KeyboardArrowUpOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                ) : (
                  <KeyboardArrowDownOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                )}
                Status da Política Municipal
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
          </Box>

          {openStatus && (
            <Box sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary">
                O município possui Política Municipal de Agricultura Familiar:
              </Typography>
              <Box sx={{ mt: .5 }}>
                <Chip
                  label={politicaLabel}
                  color={politicaColor}
                  variant="outlined"
                  sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                />
              </Box>
            </Box>
          )}
        </Paper>

        {/* Bloco: Detalhes do Instrumento Legal */}
        <Paper elevation={1} sx={{ p: 0 }}>
          <Box
            sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setOpenDetalhes(!openDetalhes)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  color: '#000000DE',
                  fontSize: '20px',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {openDetalhes ? (
                  <KeyboardArrowUpOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                ) : (
                  <KeyboardArrowDownOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                )}
                Detalhes do Instrumento Legal
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
          </Box>

          {openDetalhes && (
            <Box sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                sx={{ mb: 2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tipo do Instrumento/Dispositivo Legal
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {getTipoInstrumentoTexto(formulario?.tipo_de_instrumento)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Número da Publicação
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formulario?.numero_da_publicacao || 'Não informado'}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Data da Publicação
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatarData(formulario?.data_da_publicacao ?? null)}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Documento da Política Municipal
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {formulario?.upload_documento ? (
                    <DocumentComponent
                      uuid={formulario.upload_documento}
                      setUuid={() => { }}
                      podeSubstituir={false}
                      podeEnviar={false}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Nenhum documento disponível
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Bloco: PMAF */}
        <Paper elevation={1} sx={{ p: 0 }}>
          <Box
            sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setOpenPmaf(!openPmaf)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  color: '#000000DE',
                  fontSize: '20px',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {openPmaf ? (
                  <KeyboardArrowUpOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                ) : (
                  <KeyboardArrowDownOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                )}
                Plano Municipal da Agricultura Familiar – PMAF
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
          </Box>

          {openPmaf && (
            <Box sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary">
                O município possui o Plano Municipal da Agricultura Familiar – PMAF?
              </Typography>
              <Box sx={{ mt: .5, mb: 2 }}>
                <Chip
                  label={planoLabel}
                  color={planoColor}
                  variant="outlined"
                  sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                />
              </Box>

              {possuiPMAF === true && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Documento do Plano Municipal da Agricultura Familiar (PDF)
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <DocumentComponent
                      uuid={formulario?.upload_plano_pmaf}
                      title='Documento do Plano Municipal da Agricultura Familiar (PDF)'
                      setUuid={() => { }}
                      podeSubstituir={false}
                      podeEnviar={false}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* fallback quando não houver info */}
        {(possuiPolitica === null || typeof possuiPolitica === 'undefined') && (
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center', background: '#fafafa', border: '1px solid #eee' }}>
            <Typography variant="body1" color="text.secondary">
              Informação não disponível.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              O formulário ainda não foi preenchido.
            </Typography>
          </Paper>
        )}
      </Stack>

      <Box display="flex" justifyContent="center" mt={4} mb={2}>
        <Button
          variant="text"
          onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
          sx={{ fontWeight: 600, letterSpacing: '0.5px' }}
        >
          VOLTAR
        </Button>
      </Box>
    </ContainerPainel>
  );
};

export default FormPMAFVisualizar;
